import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Container,
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Check from "@mui/icons-material/Check";
import {
  selectPreferences,
  selectPreferencesLoading,
} from "store/PreferenceSlice";
import { useNavigate } from "react-router-dom";
import { getCompany, updateCompany } from "store/CompanySlice";
import { useNotification } from "contexts/NotificationContext";
import { useSupabaseAuth } from "contexts/SupabaseAuthContext";
import { supabase } from "services/supabase";
import CompanyLogoUploadDialog from "./components/CompanyLogoUploadDialog";

function InvoiceSettings() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { userId } = useSupabaseAuth();
  const preferences = useSelector(selectPreferences);
  const company = useSelector((state) => state.companyReducer.company);
  const loading = useSelector(selectPreferencesLoading);
  const companyLoading = useSelector((state) => state.companyReducer.loading);
  const error = useSelector((state) => state.preferenceReducer.error);
  const companyError = useSelector((state) => state.companyReducer.error);

  const [formData, setFormData] = useState({
    companyName: "",
    companyAddress: "",
    companyPhone: "",
    companyEmail: "",
    companyWebsite: "",
    companyLogoUrl: "",
    invoiceInstructions: "",
    paymentPlatform: "",
    paymentLink: "",
    termsAndConditions: "",
    invoiceFooter: "",
    attachHours: "yes",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState(null);
  const [logoUploadSuccess, setLogoUploadSuccess] = useState(false);

  useEffect(() => {
    // Only fetch company data, preferences are loaded by auth context
    if (userId) {
      dispatch(getCompany(userId));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (preferences || company) {
      const logoUrl = company?.companyLogoUrl || "";
      setFormData({
        companyName: company?.companyName || preferences?.companyName || "",
        companyAddress:
          company?.companyAddress || preferences?.companyAddress || "",
        companyPhone: company?.companyPhone || preferences?.companyPhone || "",
        companyEmail: company?.companyEmail || preferences?.companyEmail || "",
        companyWebsite:
          company?.companyWebsite || preferences?.companyWebsite || "",
        companyLogoUrl: logoUrl,
        invoiceInstructions:
          company?.invoiceInstructions ||
          preferences?.invoiceInstructions ||
          "",
        paymentPlatform:
          company?.paymentPlatform || preferences?.paymentPlatform || "",
        paymentLink: company?.paymentLink || preferences?.paymentLink || "",
        termsAndConditions:
          company?.termsAndConditions || preferences?.termsAndConditions || "",
        invoiceFooter:
          company?.invoiceFooter || preferences?.invoiceFooter || "",
        attachHours: company?.attachHours || "yes",
      });
      setLogoPreviewUrl(logoUrl);
    }
  }, [preferences, company]);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    };
  }, [logoPreviewUrl]);

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, "");

    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
      return digits.length > 0 ? `(${digits}` : digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
        6,
        10
      )}`;
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    // Format phone number field
    if (field === "companyPhone") {
      const formatted = formatPhoneNumber(value);
      setFormData({
        ...formData,
        [field]: formatted,
      });
    } else {
      setFormData({
        ...formData,
        [field]: value,
      });
    }
  };

  const handleToggleChange = (event, newValue, fieldName) => {
    if (newValue !== null) {
      setFormData({
        ...formData,
        [fieldName]: newValue,
      });
    }
  };

  const handleLogoFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedLogoFile(file);
    setLogoUploadError(null);
    setLogoUploadSuccess(false);
    const objectUrl = URL.createObjectURL(file);
    setLogoPreviewUrl((previous) => {
      if (previous?.startsWith("blob:")) {
        URL.revokeObjectURL(previous);
      }
      return objectUrl;
    });
  };

  const handleOpenLogoDialog = () => {
    setLogoUploadError(null);
    setLogoUploadSuccess(false);
    setLogoDialogOpen(true);
  };

  const resetLogoSelection = () => {
    setSelectedLogoFile(null);
    setLogoPreviewUrl((previous) => {
      if (previous?.startsWith("blob:")) {
        URL.revokeObjectURL(previous);
        return formData.companyLogoUrl || null;
      }
      return previous;
    });
  };

  const handleCloseLogoDialog = () => {
    setLogoDialogOpen(false);
    resetLogoSelection();
    setLogoUploadError(null);
    setLogoUploadSuccess(false);
  };

  const handleSaveLogo = async () => {
    if (!selectedLogoFile || !userId) return;
    setIsLogoUploading(true);
    setLogoUploadError(null);
    try {
      const fileExt = selectedLogoFile.name.split(".").pop();
      const filePath = `${userId}/${Date.now()}.${fileExt}`;

      const { error: uploadErrorResult } = await supabase.storage
        .from("logos")
        .upload(filePath, selectedLogoFile, {
          upsert: true,
          contentType: selectedLogoFile.type,
        });

      if (uploadErrorResult) {
        console.error(
          "Logo upload failed:",
          uploadErrorResult,
          filePath,
          selectedLogoFile
        );
        throw uploadErrorResult;
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from("logos").getPublicUrl(filePath);

      // Update company with new logo URL
      await dispatch(
        updateCompany({ userId, ...formData, companyLogoUrl: publicUrl })
      ).unwrap();

      setFormData({ ...formData, companyLogoUrl: publicUrl });
      setLogoPreviewUrl(publicUrl);
      setLogoUploadSuccess(true);
      resetLogoSelection();
      showNotification("Company logo updated successfully!", "success");
      handleCloseLogoDialog();
    } catch (error) {
      console.error("Logo upload failed:", error);
      setLogoUploadError(
        error.message || "Failed to upload logo. Please try again."
      );
    } finally {
      setIsLogoUploading(false);
    }
  };

  const handleClearLogo = async () => {
    if (!userId) return;
    setIsLogoUploading(true);
    setLogoUploadError(null);
    setLogoUploadSuccess(false);
    try {
      await dispatch(
        updateCompany({ userId, ...formData, companyLogoUrl: "" })
      ).unwrap();

      setFormData({ ...formData, companyLogoUrl: "" });
      setLogoPreviewUrl("");
      setLogoUploadSuccess(true);
      resetLogoSelection();
      showNotification("Company logo removed successfully!", "success");
    } catch (error) {
      console.error("Clearing logo failed:", error);
      setLogoUploadError(
        error.message || "Failed to clear logo. Please try again."
      );
    } finally {
      setIsLogoUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      // Update company information using CompanySlice
      const updatedCompany = await dispatch(
        updateCompany({ userId, ...formData })
      ).unwrap();

      // Update form data with the returned company data
      const logoUrl = updatedCompany.company_logo_url || "";
      setFormData({
        companyName: updatedCompany.company_name || "",
        companyAddress: updatedCompany.company_address || "",
        companyPhone: updatedCompany.company_phone || "",
        companyEmail: updatedCompany.company_email || "",
        companyWebsite: updatedCompany.company_website || "",
        companyLogoUrl: logoUrl,
        invoiceInstructions: updatedCompany.invoice_instructions || "",
        paymentPlatform: updatedCompany.payment_platform || "",
        paymentLink: updatedCompany.payment_link || "",
        termsAndConditions: updatedCompany.terms_and_conditions || "",
        invoiceFooter: updatedCompany.invoice_footer || "",
        attachHours: updatedCompany.attach_hours || "yes",
      });
      setLogoPreviewUrl(logoUrl);

      showNotification("Invoice settings updated successfully!", "success");
      
      // Check if user is in onboarding mode and redirect accordingly
      const setupStatus = preferences?.setupStatus;
      if (setupStatus !== "skipped" && setupStatus !== "complete") {
        // User is in onboarding, redirect back to onboarding
        navigate("/onboarding");
      }
    } catch (error) {
      console.error("Error updating invoice settings:", error);
      showNotification("Failed to update invoice settings", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || companyLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box maxWidth="lg" sx={{ mx: "auto" }}>
      <Card>
        <CardHeader title="Invoice Settings" />
        <CardContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Configure your invoice branding, company information, and payment
            settings. These settings will be displayed on all your invoices.
          </Typography>

          {(error || companyError) && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error || companyError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {/* Company Information */}

            <Typography variant="h5" gutterBottom sx={{ mb: 2.5 }}>
              Company Information
            </Typography>

            <Grid container spacing={3}>
              <Grid size={12}>
                <InputLabel>Company Logo</InputLabel>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mt: 1,
                    mb: 2,
                  }}
                >
                  <Box
                    onClick={handleOpenLogoDialog}
                    sx={{
                      position: "relative",
                      display: "inline-flex",
                      cursor: "pointer",
                      "&:hover .logo-overlay": {
                        opacity: 1,
                        visibility: "visible",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 200,
                        height: 120,
                        border: "2px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        bgcolor: "background.paper",
                      }}
                    >
                      {logoPreviewUrl ? (
                        <img
                          src={logoPreviewUrl}
                          alt={formData.companyName || "Company Logo"}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No logo
                        </Typography>
                      )}
                    </Box>
                    <Box
                      className="logo-overlay"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        bgcolor: "rgba(0,0,0,0.55)",
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "common.white",
                        gap: 0.5,
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        opacity: 0,
                        visibility: "hidden",
                        transition: "opacity 0.2s ease",
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                      Edit
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Click to upload or change your company logo. This will
                    appear on your invoices.
                  </Typography>
                </Box>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <InputLabel>Company Name</InputLabel>
                <TextField
                  fullWidth
                  value={formData.companyName}
                  onChange={handleChange("companyName")}
                  placeholder="Your Company Name"
                />
                <FormHelperText>
                  This will appear at the top of your invoices
                </FormHelperText>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <InputLabel>Company Email</InputLabel>
                <TextField
                  fullWidth
                  value={formData.companyEmail}
                  onChange={handleChange("companyEmail")}
                  placeholder="contact@yourcompany.com"
                  type="email"
                />
              </Grid>
              <Grid size={12}>
                <InputLabel>Company Address</InputLabel>
                <TextField
                  fullWidth
                  value={formData.companyAddress}
                  onChange={handleChange("companyAddress")}
                  placeholder="123 Business St, City, State 12345"
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <InputLabel>Company Phone</InputLabel>
                <TextField
                  fullWidth
                  value={formData.companyPhone}
                  onChange={handleChange("companyPhone")}
                  placeholder="(555) 555-5555"
                  type="tel"
                  inputProps={{
                    pattern: "\\([0-9]{3}\\) [0-9]{3}-[0-9]{4}",
                    maxLength: 14,
                  }}
                  helperText="Format: (XXX) XXX-XXXX"
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <InputLabel>Company Website</InputLabel>
                <TextField
                  fullWidth
                  value={formData.companyWebsite}
                  onChange={handleChange("companyWebsite")}
                  placeholder="https://yourcompany.com"
                  type="url"
                />
              </Grid>
            </Grid>

            {/* Payment Settings */}
            <Typography variant="h4" gutterBottom mb={2.5} mt={2}>
              Payment Settings
            </Typography>

            <Grid container spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <InputLabel>Payment Platform (coming soon)</InputLabel>
                <Select
                  fullWidth
                  value={formData.paymentPlatform}
                  onChange={handleChange("paymentPlatform")}
                  disabled={true}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="stripe">Stripe</MenuItem>
                  <MenuItem value="paypal">PayPal</MenuItem>
                  <MenuItem value="square">Square</MenuItem>
                  <MenuItem value="quickbooks">QuickBooks</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
                <FormHelperText>
                  Select your preferred payment platform
                </FormHelperText>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <InputLabel>Payment Link (coming soon)</InputLabel>
                <TextField
                  fullWidth
                  value={formData.paymentLink}
                  onChange={handleChange("paymentLink")}
                  disabled={true}
                  placeholder="https://your-payment-link.com"
                />
                <FormHelperText>
                  Direct link to your payment page
                </FormHelperText>
              </Grid>
              <Grid size={12}>
                <InputLabel>Payment Instructions</InputLabel>
                <TextField
                  fullWidth
                  value={formData.invoiceInstructions}
                  onChange={handleChange("invoiceInstructions")}
                  placeholder="Please pay within 30 days. You can pay online using the link above or mail a check to..."
                  multiline
                  rows={3}
                />
                <FormHelperText>
                  Instructions that will appear on your invoices
                </FormHelperText>
              </Grid>
            </Grid>

            {/* Invoice Defaults */}
            <Typography variant="h4" gutterBottom mt={2} mb={2.5}>
              Invoice Defaults
            </Typography>

            <Grid container spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <InputLabel>Attach Hours to Invoices?</InputLabel>
                <ToggleButtonGroup
                  value={formData.attachHours || "yes"}
                  exclusive
                  onChange={(event, newValue) =>
                    handleToggleChange(event, newValue, "attachHours")
                  }
                  aria-label="attach hours"
                  sx={{ width: "100%", mt: 1 }}
                >
                  <ToggleButton value="yes" aria-label="yes" sx={{ flex: 1 }}>
                    {formData.attachHours === "yes" && (
                      <Check sx={{ mr: 1 }} />
                    )}
                    Yes
                  </ToggleButton>
                  <ToggleButton value="no" aria-label="no" sx={{ flex: 1 }}>
                    {formData.attachHours === "no" && (
                      <Check sx={{ mr: 1 }} />
                    )}
                    No
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 9,
                }}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Typography variant="body2" color="text.secondary">
                  When this setting is enabled, any new client you create will automatically have hours attached to their invoices by default. If you want all clients to include hours by default, enable it here. If you prefer to manage this setting individually for each client, you can set it in the client settings instead.
                </Typography>
              </Grid>
            </Grid>

            {/* Terms and Footer */}

            <Typography variant="h4" gutterBottom mt={2} mb={2.5}>
              Terms and Conditions
            </Typography>

            <Grid container spacing={3}>
              <Grid size={12}>
                <InputLabel>Terms and Conditions</InputLabel>
                <TextField
                  fullWidth
                  value={formData.termsAndConditions}
                  onChange={handleChange("termsAndConditions")}
                  placeholder="Payment is due within 30 days of invoice date. Late payments may incur additional fees..."
                  multiline
                  rows={4}
                />
                <FormHelperText>
                  Legal terms that will appear on your invoices
                </FormHelperText>
              </Grid>
              <Grid size={12}>
                <InputLabel>Invoice Footer</InputLabel>
                <TextField
                  fullWidth
                  value={formData.invoiceFooter}
                  onChange={handleChange("invoiceFooter")}
                  placeholder="Thank you for your business!"
                  multiline
                  rows={2}
                />
                <FormHelperText>
                  Optional footer message for your invoices
                </FormHelperText>
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {isSubmitting ? "Saving..." : "Save Invoice Settings"}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <CompanyLogoUploadDialog
        open={logoDialogOpen}
        logoSrc={logoPreviewUrl}
        companyName={formData.companyName}
        isUploading={isLogoUploading}
        uploadError={logoUploadError}
        uploadSuccess={logoUploadSuccess}
        onClose={handleCloseLogoDialog}
        onFileChange={handleLogoFileChange}
        onSave={handleSaveLogo}
        canSave={Boolean(selectedLogoFile)}
        onClear={handleClearLogo}
        canClear={!isLogoUploading && Boolean(logoPreviewUrl)}
      />
    </Box>
  );
}

export default InvoiceSettings;
